import { SpecialAnchorTypes, ToolBaseTypes, ToolTypes } from '@labradorsports/constants';

import FieldPiece from '../models/field-piece.js';
import { angleBetweenPoints, applyOffset, getFieldTypeCenter, translateAtAngle } from '../utils/index.js';

import { PluginContext } from './api.js';

export default {
    baseType: ToolBaseTypes.PLAYER,
    types: [ToolTypes.BLUEPLAYER, ToolTypes.ORANGEPLAYER, ToolTypes.COACH],
    specialAnchorTypes: [SpecialAnchorTypes.PLAYER_ROTATE, SpecialAnchorTypes.PEN_PLAYER],
    generateAnchors: ({ play, frame }) => {
        const anchors = [];

        if (play.fieldSetup) {
            anchors.unshift(
                ...frame.players
                    .filter((atk: any) => typeof atk.props?.pieceId !== 'number')
                    .map((atk: any) => ({
                        target: atk.id,
                        ...atk,
                        ...atk.origin,
                    }))
            );
        } else {
            anchors.unshift(
                ...frame.players.map((atk: any) => ({
                    target: atk.id,
                    ...atk,
                    ...atk.origin,
                    pointerType: 'pen',
                    type: SpecialAnchorTypes.PEN_PLAYER,
                }))
            );

            anchors.unshift(
                ...frame.goalies.map((atk: any) => ({
                    target: atk.id,
                    ...atk,
                    ...atk.origin,
                    pointerType: 'pen',
                    type: SpecialAnchorTypes.PEN_PLAYER,
                    space: 'screen' as const,
                }))
            );

            anchors.unshift(
                ...frame.players
                    .map((plr) => {
                        if (typeof plr.rotation === 'number') {
                            return {
                                ...plr,
                                ...plr.origin,
                                target: plr.id,
                                type: SpecialAnchorTypes.PLAYER_ROTATE,
                                ...translateAtAngle(plr.origin, plr.rotation, play.field.getDiagonal() / 40),
                                space: 'screen' as const,
                            };
                        }

                        return null;
                    })
                    .filter(Boolean)
            );
        }

        return anchors;
    },

    handleDragEnd: ({ api, frame }, anchor, offset, clamped) => {
        if (anchor.type === SpecialAnchorTypes.PLAYER_ROTATE) {
            api.updatePlayerRotation({
                id: anchor.id,
                rotation: angleBetweenPoints(frame.getPlayer(anchor.id).origin, offset),
            });
        } else if (anchor.type === SpecialAnchorTypes.PEN_PLAYER) {
            // Do nothing for now
        } else {
            api.updatePlayer(anchor.id, {
                type: anchor.type,
                origin: {
                    x: clamped.x,
                    y: clamped.y,
                },
            });
        }
    },

    handleDrag: ({ frame }, anchor, offset) => {
        if (anchor.type === SpecialAnchorTypes.PLAYER_ROTATE) {
            frame.updatePlayerRotation(anchor.id, angleBetweenPoints(frame.getPlayer(anchor.id).origin, offset));
        } else if (anchor.type === SpecialAnchorTypes.PEN_PLAYER) {
            // Do nothing for now
        } else {
            frame.updatePlayer(anchor.id, offset);
        }
    },

    getDefaultMove({ play }, type, offset) {
        if (offset !== undefined) {
            return new FieldPiece({
                type,
                origin: play.field.clampCoords(offset),
            });
        }

        const base = getFieldTypeCenter(play.field, play.fieldType, play.config.FieldTypes);
        const yAdjustment = {
            [ToolTypes.BLUEPLAYER]: 4,
            [ToolTypes.ORANGEPLAYER]: -4,
        }[type];

        return new FieldPiece({
            type,
            origin: play.field.clampCoords(applyOffset(base, { x: 0, y: yAdjustment ?? 0 })),
        });
    },
} as PlayPlugin<PluginContext>;
